import * as React from 'react';
import { useState, useCallback, memo } from 'react';
import { isFunction } from '../helpers/utils';
import { clsx } from 'clsx';

import type { EditorState, LexicalEditor } from 'lexical';
import { $generateHtmlFromNodes } from "@lexical/html";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';

import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin';
import type { EditorProps, EditorPropsColors } from "./editorProps.ts";

import CustomLinkPlugin from "./plugins/customLinkPlugin";
import FloatingLinkEditorPlugin from "./plugins/FloatingLinkEditorPlugin";
import HorizontalRulePlugin from "./plugins/horizontalRulePlugin";
import InlineImagePlugin from './plugins/InlineImagePlugin';
import ListMaxIndentLevelPlugin from "./plugins/ListMaxIndentLevelPlugin";
import ToolbarClassPlugin from "./plugins/toolbarClassPlugin";
import ToolbarHistoryPlugin from "./plugins/toolbarHistoryPlugin";
import ToolbarInsertPlugin from "./plugins/toolbarInsertPlugin";
import ToolbarPlugin from "./plugins/toolbarPlugin";

type Props = EditorPropsColors & EditorProps & {}

function Editor({
	sectionClasses = "",
	background = "",
	backgroundClass = "",
	updateHtml,
	updateState,
	color,
	textClass,
	config,
	...otherProps
}: Props) {
	const [floatingAnchorElem, setFloatingAnchorElem] = useState<HTMLDivElement | null>(null);
	const [isLinkEditMode, setIsLinkEditMode] = useState<boolean>(false);

	const onRef = (_floatingAnchorElem: HTMLDivElement) => {
		if (_floatingAnchorElem !== null) {
			setFloatingAnchorElem(_floatingAnchorElem);
		}
	};

	const onChange = useCallback((editorState: EditorState, editor: LexicalEditor) => {
		if (updateHtml && isFunction(updateHtml)) {
			editor.update(() => {
				updateHtml($generateHtmlFromNodes(editor, null));
			});
		}
		if (updateState && isFunction(updateState)) {
			editorState.read(() => {
				updateState(editorState.toJSON());
			});
		}
	}, [updateHtml, updateState]);

	let contentStyle: React.CSSProperties = {};
	if (!backgroundClass) {
		contentStyle.backgroundColor = background;
	}
	if (!textClass) {
		contentStyle.color = color;
	}

	return (<>
		<LexicalComposer initialConfig={config}>
			<div className="editor-container">
				<ToolbarHistoryPlugin/>
				<ToolbarPlugin setIsLinkEditMode={setIsLinkEditMode}/>
				<ToolbarInsertPlugin/>
				<ToolbarClassPlugin/>
				<div className="editor-inner">
					<HistoryPlugin/>
					<InlineImagePlugin/>
					<TabIndentationPlugin/>
					{floatingAnchorElem && <FloatingLinkEditorPlugin
						anchorElem={floatingAnchorElem}
						isLinkEditMode={isLinkEditMode}
						setIsLinkEditMode={setIsLinkEditMode}
					/>}
					<ListPlugin/>
					<ListMaxIndentLevelPlugin maxDepth={3}/>
					<HorizontalRulePlugin/>
					<CustomLinkPlugin/>
					<OnChangePlugin onChange={onChange} ignoreSelectionChange={true}/>

					<RichTextPlugin
						contentEditable={
							<div className="editor-scroller">
								<div className="editor" ref={onRef}>
									<ContentEditable
										className={clsx("editor-input", sectionClasses, backgroundClass, textClass)}
										style={contentStyle}
									/>
								</div>
							</div>
						}
						placeholder={null}
						ErrorBoundary={LexicalErrorBoundary}
					/>
				</div>
			</div>
		</LexicalComposer>
	</>);
}

export default memo(Editor);
